/**
 * DialogBulkUpload — Main Wizard
 *
 * 6-step dialog for bulk uploading spreadsheet data into any V2 entity.
 * Uses MUI Stepper + DialogActions pattern from the project.
 */

// react
import React, { useEffect } from 'react';

// MUI
import Box from '@mui/material/Box';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Alert from '@mui/material/Alert';

// i18n
import { useTranslation } from 'react-i18next';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// hook
import { useBulkUpload, UseBulkUploadParams } from './useBulkUpload';

// steps
import { StepSelectTemplate } from './tabs/StepSelectTemplate';
import { StepUploadFile } from './tabs/StepUploadFile';
import { StepMapColumns } from './tabs/StepMapColumns';
import { StepResolveRelations } from './tabs/StepResolveRelations';
import { StepValidatePreview } from './tabs/StepValidatePreview';
import { StepSubmit } from './tabs/StepSubmit';

// types
import { BULK_UPLOAD_STEPS, STEP_LABEL_KEYS } from './utils/types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DialogBulkUploadProps extends UseBulkUploadParams {
	isOpen: boolean;
	onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DialogBulkUpload: React.FC<DialogBulkUploadProps> = ({
	isOpen,
	onClose,
	schema,
	projectId,
	lookupData,
	customFieldDefinitions,
	onSuccess,
	onLookupDataRefresh,
	batchSize,
}) => {
	const { t } = useTranslation();

	const bulk = useBulkUpload({
		schema,
		projectId,
		lookupData,
		customFieldDefinitions,
		onSuccess,
		onLookupDataRefresh,
		onClose,
		batchSize,
	});

	// Load templates when dialog opens
	useEffect(() => {
		if (isOpen) {
			bulk.loadTemplates();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	// Reset state when dialog closes
	useEffect(() => {
		if (!isOpen) {
			bulk.reset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	// Build footer actions based on current step
	const actions = [];

	if (bulk.currentStep !== 'submit' || bulk.progress.status === 'idle') {
		actions.push({
			children: t('cancel'),
			onClick: () => {
				bulk.reset();
				onClose();
			},
		});
	}

	if (bulk.canGoBack && bulk.progress.status !== 'submitting') {
		actions.push({
			children: t('back'),
			onClick: bulk.goBack,
			variant: 'outlined' as const,
		});
	}

	if (bulk.currentStep === 'submit') {
		if (bulk.progress.status === 'idle') {
			actions.push({
				children: t('bulkUpload.submitRows', { count: bulk.validationStats?.valid ?? 0 }),
				onClick: bulk.submit,
				variant: 'contained' as const,
			});
		} else if (bulk.progress.status === 'complete') {
			actions.push({
				children: t('close'),
				onClick: () => {
					bulk.reset();
					onClose();
				},
				variant: 'contained' as const,
			});
		}
	} else {
		actions.push({
			children:
				bulk.currentStep === 'validate-preview' ? t('bulkUpload.reviewAndSubmit') : t('next'),
			onClick: bulk.goNext,
			disabled: !bulk.canGoNext,
			variant: 'contained' as const,
		});
	}

	// Render current step content
	const renderStep = () => {
		switch (bulk.currentStep) {
			case 'select-template':
				return (
					<StepSelectTemplate
						templates={bulk.templates}
						selectedTemplate={bulk.selectedTemplate}
						isLoading={bulk.isLoadingTemplates}
						onSelect={bulk.selectTemplate}
						onDownloadTemplate={bulk.handleDownloadTemplate}
						schema={schema}
					/>
				);
			case 'upload-file':
				return (
					<StepUploadFile
						file={bulk.file}
						parseResult={bulk.parseResult}
						isLoading={bulk.isParsingFile}
						error={bulk.parseError}
						onFileSelect={bulk.handleFileSelect}
						onSheetChange={bulk.handleSheetChange}
						onHeaderRowChange={bulk.handleHeaderRowChange}
					/>
				);
			case 'map-columns':
				return (
					<StepMapColumns
						mappings={bulk.mappings}
						fields={bulk.effectiveSchema.fields}
						headers={bulk.parseResult?.headers ?? []}
						onUpdateMapping={bulk.updateMapping}
						contextDefaults={bulk.parseResult?.contextDefaults ?? []}
					/>
				);
			case 'resolve-relations':
				return (
					<StepResolveRelations
						groups={bulk.resolutionGroups}
						onResolve={bulk.resolveGroup}
						onSkip={bulk.skipGroup}
						onAutoResolve={bulk.autoResolveHighConfidence}
						onCreateRecord={bulk.createRelatedRecord}
						unresolvedCount={bulk.unresolvedCount}
						lookupData={bulk.mergedLookupData}
					/>
				);
			case 'validate-preview':
				return (
					<StepValidatePreview
						results={bulk.validationResults}
						stats={bulk.validationStats}
						fields={bulk.effectiveSchema.fields}
						lookupData={bulk.mergedLookupData}
						onToggleSkip={bulk.toggleRowSkip}
						onEditCell={bulk.editCell}
					/>
				);
			case 'submit':
				return (
					<StepSubmit
						progress={bulk.progress}
						onSaveTemplate={bulk.saveTemplate}
						onRetryFailed={bulk.retryFailed}
						onExportFailed={bulk.exportFailedRows}
						stats={bulk.validationStats}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<DialogActions
			actions={actions}
			hasClose={bulk.progress.status !== 'submitting'}
			isOpen={isOpen}
			maxWidth={900}
			onClose={(_, reason) => {
				if (reason === 'backdropClick' && bulk.progress.status === 'submitting') return;
				bulk.reset();
				onClose();
			}}
			title={{
				children: t('bulkUpload.title', { label: schema.label }),
				variant: 'h3',
			}}>
			{/* Stepper */}
			<Box sx={{ mb: 3 }}>
				<Stepper activeStep={bulk.currentStepIndex} alternativeLabel>
					{BULK_UPLOAD_STEPS.map((step) => (
						<Step key={step}>
							<StepLabel>{t(STEP_LABEL_KEYS[step])}</StepLabel>
						</Step>
					))}
				</Stepper>
			</Box>

			{/* Error banner */}
			{bulk.error && (
				<Alert severity="error" onClose={bulk.clearError} sx={{ mb: 2 }}>
					{bulk.error}
				</Alert>
			)}

			{/* Step content */}
			<Box sx={{ minHeight: 300 }}>{renderStep()}</Box>
		</DialogActions>
	);
};
